/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { CreateExternalUser } from "../../../api/ExternalUser";
import { UserAtom } from "../../../atoms/UserAtom";
import { IExternalUser } from "../../../types/ExternalUserTypes";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;
  margin: 20px -22px 0px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 400px;
  overflow-y: auto;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid #eceeee;
  padding: 20px 20px 0px;
`;

const InputLabel = styled.div`
  display: flex;
  flex-direction: column;
  width: 370px;
  margin-top: 20px;
  padding: 0px 20px;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 400;
  margin-bottom: 5px;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  color: #fb972e;
  font-weight: 400;
  margin-bottom: 5px;
  cursor: pointer;
`;

const TagsContainer = styled.div`
  width: 370px;
  margin-top: 20px;
  padding: 20px 20px 0px;
  border-top: 1px solid #eceeee;
  display: flex;
  flex-direction: column;
`;

const LabelContainer = styled.div`
  width: 370px;
  margin-top: 20px;
  padding: 20px;
  border-top: 1px solid #eceeee;
  display: flex;
  justify-content: space-between;
`;

const ActionIcon = styled(motion.img)`
  width: 10px;
  height: 10px;
`;

const marginRight5 = css`
  margin-right: 5px;
`;

const marginLeft5 = css`
  margin-left: 5px;
`;

export enum ShowTagState {
  ADD,
  SHOW,
  HIDE,
}

type Props =
  | {
      isEdit: true;
      user: IExternalUser;
      updateUsers: () => void;
    }
  | {
      isEdit?: undefined;
      updateUsers: () => void;
    };

export default function AddUser(props: Props): JSX.Element {
  const modal = useModal();

  const { updateUsers } = props;

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const user = useRecoilValue(UserAtom);

  useEffect(() => {
    if (props.isEdit) {
      const { user } = props as any;
      setName(user.name);
      setPassword(user.password);
    }
  }, []);

  async function handleCreate() {
    if (!user || !name || isLoading) return;

    setIsLoading(true);

    const response = await CreateExternalUser({
      mainUserUid: user.userUid,
      name,
    });

    if (response.status === "ok") {
      updateUsers();
      modal.popModal();
    } else {
      console.log(response.message);
    }

    setIsLoading(false);
  }

  return (
    <ModalBody>
      <Wrapper>
        <InputLabel>
          <Title>First Name</Title>
          <StyledInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Name"
          />
        </InputLabel>

        <InputLabel>
          <Title>Passcode</Title>
          <StyledInput
            disabled
            value={password}
            placeholder="Automatically generated"
          />
        </InputLabel>
      </Wrapper>

      <Buttons>
        <StyledButton
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
          onClick={() => modal.popModal()}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 80px;
          `}
          isLoading={isLoading}
          disable={isLoading || !name}
          onClick={handleCreate}
        >
          {props.isEdit ? "Save" : "Add"}
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
